import { useState } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { useAccount } from '@azure/msal-react';
import { useBuildVars } from 'src/config';
import { useApiQuery, useApiPost } from 'src/api';
import useDebouncedState from 'src/hooks/useDebouncedState';
import { Customer } from './customer-search';
import { Vendor } from './vendor-search';

export const URLS = {
  CUSTOMERS_SEARCH: '/GPEPricingRuleRequestServiceUS/customers/search',
  PROGRAMS_SEARCH: '/GPEPricingRuleRequestServiceUS/programs/search',
  PROGRAM_ATTRIBUTES_SEARCH:
    '/GPEPricingRuleRequestServiceUS/programs/{programId}/attributes',
  SEGMENTS_SEARCH: '/GPEPricingRuleRequestServiceUS/segments/search',
  SEGMENT_LOOKUP: '/GPEPricingRuleRequestServiceUS/segmentLookUp',
  VALIDATE_BCN: '/gpe/validateEscalationEmailForBCNApprover',
  VALIDATE_VENDOR: '/gpe/validateEscalationEmailForVendorApprover',
  VENDORS_SEARCH: '/GPEPricingRuleRequestServiceUS/vendors/search',
};
const MIN_CHAR_TO_SEARCH = 2;
export const VALIDATION_RESULT = {
  ALLOWED: 'ALLOWED',
  NOT_ALLOWED: 'NOT ALLOWED',
  NOT_ALLOWED_ESCALATION: 'NOT ALLOWED - Escalation',
};
export const PRICE_CONSTS = {
  CUSTOMER: 'customer',
  PROGRAM: 'program',
  SEGMENT: 'segment',
  VENDOR: 'vendor',
};
export type EmailValidationResponse = {
  result:
    | typeof VALIDATION_RESULT.ALLOWED
    | typeof VALIDATION_RESULT.NOT_ALLOWED
    | typeof VALIDATION_RESULT.NOT_ALLOWED_ESCALATION;
};
export interface SearchFilter {
  field?: string;
  initialFilterField?: string;
  value: string;
}
export interface QuerySearch {
  filter: SearchFilter;
  page: string;
  size: string;
}
export function useCustomersSearchQuery(query: QuerySearch) {
  const [searchParams] = useSearchParams();
  const companyCode = searchParams.get('companyCode')?.toUpperCase() ?? 'MD';
  const countryCode = searchParams.get('countryCode')?.toUpperCase() ?? 'US';
  const url =
    query?.filter?.field === PRICE_CONSTS.SEGMENT
      ? URLS.SEGMENTS_SEARCH
      : URLS.CUSTOMERS_SEARCH;
  const params = {
    companyCode,
    countryCode,
    page: query.page,
    searchText: query?.filter?.value || '',
    size: query.size,
    url,
  };
  const parseResponse = (res: any) => {
    return res.json().then((list: any) => {
      return {
        list:
          query?.filter.field === PRICE_CONSTS.SEGMENT
            ? list.segments
            : list.customers,
      };
    });
  };
  const buildVars = useBuildVars();
  return useApiQuery<any>(
    url,
    params,
    {
      cacheTime: 0,
      enabled: params.searchText.length >= MIN_CHAR_TO_SEARCH,
    },
    undefined,
    {
      host: buildVars.GPE__API_BASE,
      parseResponse,
    }
  );
}
export function useVendorsSearchQuery(query: QuerySearch) {
  const [searchParams] = useSearchParams();
  const companyCode = searchParams.get('companyCode')?.toUpperCase() ?? 'MD';
  const countryCode = searchParams.get('countryCode')?.toUpperCase() ?? 'US';
  const url =
    query?.filter.field === PRICE_CONSTS.PROGRAM
      ? URLS.PROGRAMS_SEARCH
      : URLS.VENDORS_SEARCH;
  const params = {
    companyCode,
    countryCode,
    page: query.page,
    searchText: query?.filter?.value || '',
    size: query.size,
    url,
  };
  const parseResponse = (res: any) => {
    return res.json().then((list: any) => {
      return {
        list:
          query?.filter.field === PRICE_CONSTS.PROGRAM
            ? list.programs
            : list.vendors,
      };
    });
  };
  const buildVars = useBuildVars();
  return useApiQuery<any>(
    url,
    params,
    {
      cacheTime: 0,
      enabled: params.searchText.length >= MIN_CHAR_TO_SEARCH,
    },
    undefined,
    {
      host: buildVars.GPE__API_BASE,
      parseResponse,
    }
  );
}
export function useProgramAttributesSearchQuery(query: string) {
  const [searchParams] = useSearchParams();
  const companyCode = searchParams.get('companyCode')?.toUpperCase() ?? 'MD';
  const countryCode = searchParams.get('countryCode')?.toUpperCase() ?? 'US';
  const url = URLS.PROGRAM_ATTRIBUTES_SEARCH.replace('{programId}', query);
  const params = {
    companyCode,
    countryCode,
  };
  const parseResponse = (res: any) => {
    return res.json().then((list: any) => {
      return {
        list: list.attributes,
      };
    });
  };
  const buildVars = useBuildVars();
  return useApiQuery<any>(
    url,
    params,
    {
      enabled: !!query,
    },
    undefined,
    {
      host: buildVars.GPE__API_BASE,
      parseResponse,
    }
  );
}
export const API_DEBOUNCE_TIME = 300;
export function useSeachQueryParams({
  initialFilterField,
  page: initialPage,
  size: initialSize,
}: any) {
  const [searchQuery, setSearchQuery] = useState({
    filter: {
      field: initialFilterField,
      value: '',
    },
    page: initialPage,
    size: initialSize,
  });
  function extendSearchQuery(props: any) {
    setSearchQuery({
      ...searchQuery,
      ...props,
    });
  }
  return {
    searchQuery: useDebouncedState<any>(searchQuery, API_DEBOUNCE_TIME),
    setFilter: (filter: any) => extendSearchQuery({ filter, page: 0 }),
  };
}
export type SegmentResponse = {
  segmentId: number;
};
export function useSegmentLookupQuery(query: Customer | null) {
  const buildVars = useBuildVars();
  const apiPost = useApiPost<SegmentResponse[]>({
    host: buildVars.GPE__API_BASE,
  });
  const params = {
    branchCustomerNumbers: [
      `${query?.customerBranchNumber}-${query?.customerNumber}`,
    ],
    typeName: 'Customer-Owned',
  };
  return useQuery<SegmentResponse[]>(
    [URLS.SEGMENT_LOOKUP],
    () => apiPost(URLS.SEGMENT_LOOKUP, params, undefined, {}),
    {
      cacheTime: 0,
      enabled: !!query?.id,
    }
  );
}
export function useBCNAllowedQuery(customer: Customer | null) {
  const userName = useAccount()?.username ?? '';
  const params = {
    bcn: `${customer?.customerBranchNumber}${customer?.customerNumber}`,
    email: userName,
  };
  return useAllowedQuery(URLS.VALIDATE_BCN, params, !!customer?.id);
}
export function useVendorAllowedQuery(vendor: Vendor | null) {
  const userName = useAccount()?.username ?? '';
  const params = {
    email: userName,
    vendorCode: vendor?.vendorCode,
  };
  return useAllowedQuery(URLS.VALIDATE_VENDOR, params, !!vendor?.id);
}
function useAllowedQuery(url: string, params: any, enabled: boolean) {
  const buildVars = useBuildVars();
  const apiPost = useApiPost<EmailValidationResponse>({
    host: buildVars.PRICING_API_BASE,
  });
  return useQuery<EmailValidationResponse>(
    [URLS.VALIDATE_VENDOR],
    () => apiPost(url, params, undefined, {}),
    {
      cacheTime: 0,
      enabled,
    }
  );
}
